import { Component, computed, effect, inject, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BtnLoaderDirective, DropdownData, GenericDropdownComponent, SvgIconsComponent } from '@zayan-erp/shared';
import { BuilderReferenceService } from '@zayan-erp/home-building';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { map, of } from 'rxjs';
import { AddendumService } from '@zayan-erp/sales';
import { CreateAddendumPayload, AddendumCreatedEvent } from '../../../../models/deal-options.models';

@Component({
  selector: 'app-add-addendum-modal',
  imports: [ReactiveFormsModule, SvgIconsComponent, GenericDropdownComponent, BtnLoaderDirective],
  templateUrl: './add-addendum-modal.component.html'
})
export class AddAddendumModalComponent {
  private readonly modalRef = inject(NgbActiveModal);
  private readonly fb = inject(FormBuilder);
  private readonly builderReferenceService = inject(BuilderReferenceService);
  private readonly addendumService = inject(AddendumService);

  readonly lotId = signal<string | null>(null);
  readonly dealId = signal<string | null>(null);
  readonly submitPayload = signal<CreateAddendumPayload | null>(null);

  readonly builderReferences = toSignal(
    this.builderReferenceService
      .getBuilderReferences()
      .pipe(map(references => references.map<DropdownData>(ref => ({ id: ref.id, firstLabel: ref.reference })))),
    { initialValue: [] }
  );

  readonly form = this.fb.group({
    builderReference: [null, Validators.required]
  });

  readonly createResource = rxResourceFixed({
    params: () => this.submitPayload(),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params) {
        return of(null);
      }
      return this.addendumService.createAddendum(params);
    }
  });

  readonly isSubmitting = computed(() => this.createResource.isLoading());

  saved = output<AddendumCreatedEvent>();

  constructor() {
    effect(() => {
      const result = this.createResource.value();
      const payload = this.submitPayload();

      if (result !== undefined && result !== null && payload) {
        const selectedReference = this.builderReferences().find(ref => ref.id === payload.builderReferenceId);
        const referenceLabel = selectedReference?.firstLabel ?? '';

        this.saved.emit({
          builderReferenceId: payload.builderReferenceId,
          label: referenceLabel || payload.builderReferenceId
        });
        this.modalRef.close();
      }
    });
  }

  setLotId(lotId: string | null): void {
    this.lotId.set(lotId);
  }

  setDealId(dealId: string | null): void {
    this.dealId.set(dealId);
  }

  close(): void {
    this.modalRef.close();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.controls.builderReference.value;
    if (value === null) {
      return;
    }

    const lotId = this.lotId();
    if (!lotId) {
      return;
    }

    const dealId = this.dealId();
    const payload: CreateAddendumPayload = {
      builderReferenceId: value,
      lotId
    };

    if (dealId) {
      payload.dealId = dealId;
    }

    this.submitPayload.set(payload);
  }
}
